import { type MouseEvent, useEffect, useRef } from 'react';
import type { LeagueTabKey } from '@league/shared/contract';
import { Link, NavLink } from 'react-router-dom';

import { useCapabilities } from '../../lib/capabilities';

/*
  The Kit tab is absent rather than filtered: `TABS` is `as const` and read as a literal tuple
  elsewhere, so a conditional entry would widen its type and lose that. `features.ts` records why
  kit is out of V1; putting it back means one line here and flipping the flag.
*/
export const TABS = [
  { key: 'overview', to: '', label: 'Overview', end: true },
  { key: 'roster', to: 'roster', label: 'Roster' },
  /* An instructor's Roster (2026-11-01): `/api/me` sends one or the other, never both. */
  { key: 'contacts', to: 'contacts', label: 'Roster' },
  { key: 'registrations', to: 'registrations', label: 'Registrations' },
  { key: 'seating', to: 'seating', label: 'Seating' },
  { key: 'scores', to: 'scores', label: 'Scores' },
  { key: 'standings', to: 'standings', label: 'Standings' },
  { key: 'emails', to: 'emails', label: 'Emails' },
  { key: 'close-out', to: 'close-out', label: 'Close-out' },
  { key: 'settings', to: 'settings', label: 'Settings' },
] as const satisfies readonly { key: LeagueTabKey; to: string; label: string; end?: boolean }[];

/**
 * THE LEAGUE'S TABS, wherever she is in that league — owner, 2026-10-15: keep the tabs on the
 * session's score sheet and seating board too, so getting from scores to seating is one tap rather
 * than back to the league first.
 *
 * `current` marks the tab a session page belongs to: its address is under `/director/sessions`, so
 * no tab's own link matches it. `onLeave` lets a page with unsaved work ask first.
 *
 * ⚠ EVERY TAB GOES TO THE LEAGUE'S OWN PAGE, from anywhere. Seating and Scores once opened the
 * session's board and sheet when on a session page — so from a session's scores, Scores opened the
 * same page and there was no way back to the league's list of sessions (owner, 2026-10-15).
 */
export function LeagueTabs({
  leagueId,
  current,
  onLeave,
  className = '',
}: {
  leagueId: string;
  current?: (typeof TABS)[number]['label'] | undefined;
  onLeave?: (() => boolean) | undefined;
  className?: string;
}) {
  const guard = (event: MouseEvent) => {
    if (onLeave && !onLeave()) event.preventDefault();
  };
  /*
    On a phone the strip is wider than the screen, and the tab she is on can be off to the right —
    Scores, from a session's score sheet. Scroll the strip (only the strip) to show it.
  */
  const strip = useRef<HTMLElement>(null);
  /* Only the tabs `/api/me` names — an instructor's five (2026-10-16, 2026-11-01). */
  const { leagueTabs } = useCapabilities();
  useEffect(() => {
    const nav = strip.current;
    const active = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !active) return;
    if (active.offsetLeft + active.offsetWidth > nav.scrollLeft + nav.clientWidth || active.offsetLeft < nav.scrollLeft) {
      nav.scrollLeft = active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2;
    }
  }, [current, leagueId]);
  return (
    /*
      Scrolls sideways rather than wrapping. Nine tabs will not fit on a phone at any size worth
      reading, and a strip that wraps to three rows pushes the actual page below the fold — which
      on the roster tab is the thing she came for. The negative margin lets it bleed to the screen
      edge so the scroll is discoverable instead of looking like a clipped row.
    */
    <nav ref={strip} aria-label="League" className={`relative flex gap-1 overflow-x-auto border-b border-light ${className}`}>
      {TABS.filter((tab) => leagueTabs.includes(tab.key)).map((tab) => {
        const to = `/director/leagues/${leagueId}${tab.to ? `/${tab.to}` : ''}`;
        const style = (active: boolean) =>
          [
            'shrink-0 whitespace-nowrap border-b-2 px-3 py-2.5 text-[14px] transition-colors',
            active ? 'border-deep font-medium text-ink' : 'border-transparent text-ink-2 hover:text-ink',
          ].join(' ');
        /*
          THE LABEL RESERVES ITS OWN BOLD WIDTH. The active tab is a heavier weight, and heavier text
          is wider — so without this every tab after the active one slides a pixel or two sideways.
          A ghost copy at the active weight sets the width; the visible copy sits on top of it.
        */
        const label = (
          <span className="grid">
            <span aria-hidden="true" className="invisible col-start-1 row-start-1 font-medium">
              {tab.label}
            </span>
            <span className="col-start-1 row-start-1">{tab.label}</span>
          </span>
        );
        /*
          ⚠ ON A SESSION'S PAGE THE TAB IT BELONGS TO IS A PLAIN LINK MARKED CURRENT. `NavLink` sets
          `aria-current` only when its own address matches, which a session page's never does — so
          the underlined tab was not announced, nor scrolled into view on a phone.
        */
        return current === tab.label ? (
          <Link key={tab.label} to={to} onClick={guard} aria-current="page" className={style(true)}>
            {label}
          </Link>
        ) : (
          <NavLink key={tab.label} to={to} end={'end' in tab ? tab.end : false} onClick={guard} className={({ isActive }) => style(isActive)}>
            {label}
          </NavLink>
        );
      })}
    </nav>
  );
}

